import React from "react";
import { GraduationCap, Clock, Calendar, ArrowRight, BookOpen } from "lucide-react";
import { Button } from "./ui/button";
import { formations, programModules, heroImages } from "../mock";

export default function Formations() {
  return (
    <section id="cap-aepe" className="py-20 lg:py-28 bg-[#f5f1e8]/60 relative overflow-hidden">
      <div className="blob w-[420px] h-[420px] bg-[#2c7a80]/15 top-1/2 -right-32" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-8">
            <span className="text-xs uppercase tracking-widest text-[#2c7a80] font-semibold">Nos formations</span>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium text-[#0e3a3f] leading-[1.05] mt-3 title-underline">
              Le CAP AEPE,<br />
              6 parcours au choix.
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-[#0e3a3f]/75 leading-relaxed">
              Le <strong>CAP Accompagnant Éducatif Petite Enfance</strong> est le diplôme de référence pour travailler auprès d'enfants de 0 à 6 ans. Choisissez la voie qui correspond à votre situation.
            </p>
          </div>
        </div>

        {/* Formations cards */}
        <div id="formations" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-24">
          {formations.map((f, idx) => (
            <div
              key={f.id}
              className={`relative rounded-3xl p-8 hover-lift ${
                idx === 0
                  ? "bg-[#0e3a3f] text-white"
                  : "bg-white border border-[#e6ded0]"
              }`}
            >
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                idx === 0 ? "bg-[#e8a04d] text-white" : "bg-[#2c7a80]/10 text-[#2c7a80]"
              }`}>
                {f.badge}
              </div>
              <h3 className={`font-display text-2xl font-medium mt-5 mb-2 ${idx === 0 ? "text-white" : "text-[#0e3a3f]"}`}>
                {f.title}
              </h3>
              <p className={`text-sm mb-6 ${idx === 0 ? "text-white/70" : "text-[#0e3a3f]/60"}`}>
                {f.subtitle}
              </p>

              <div className={`grid ${f.rhythm ? "grid-cols-2" : "grid-cols-1"} gap-4 py-5 border-y ${
                idx === 0 ? "border-white/15" : "border-[#e6ded0]"
              }`}>
                <div>
                  <div className={`flex items-center gap-1.5 text-xs uppercase tracking-wider mb-1 ${
                    idx === 0 ? "text-white/50" : "text-[#0e3a3f]/50"
                  }`}>
                    <Clock className="w-3 h-3" /> Durée
                  </div>
                  <div className={`font-semibold ${idx === 0 ? "text-white" : "text-[#0e3a3f]"}`}>{f.duration}</div>
                </div>
                {f.rhythm && (
                  <div>
                    <div className={`flex items-center gap-1.5 text-xs uppercase tracking-wider mb-1 ${
                      idx === 0 ? "text-white/50" : "text-[#0e3a3f]/50"
                    }`}>
                      <Calendar className="w-3 h-3" /> Rythme
                    </div>
                    <div className={`font-semibold ${idx === 0 ? "text-white" : "text-[#0e3a3f]"}`}>{f.rhythm}</div>
                  </div>
                )}
              </div>

              <ul className="space-y-2.5 my-6">
                {f.highlights.map((h) => (
                  <li key={h} className={`flex items-start gap-2 text-sm ${
                    idx === 0 ? "text-white/85" : "text-[#0e3a3f]/80"
                  }`}>
                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                      idx === 0 ? "bg-[#e8a04d]" : "bg-[#2c7a80]"
                    }`} />
                    {h}
                  </li>
                ))}
              </ul>

              <div className={`flex items-center justify-between pt-4 border-t ${
                idx === 0 ? "border-white/15" : "border-[#e6ded0]"
              }`}>
                <div>
                  <div className={`text-xs ${idx === 0 ? "text-white/50" : "text-[#0e3a3f]/50"}`}>Tarif</div>
                  <div className={`font-display text-xl font-medium ${idx === 0 ? "text-white" : "text-[#0e3a3f]"}`}>
                    {f.price}
                  </div>
                </div>
                <Button
                  asChild
                  size="sm"
                  className={`rounded-full ${
                    idx === 0
                      ? "bg-white text-[#0e3a3f] hover:bg-[#f5f1e8]"
                      : "bg-[#2c7a80] text-white hover:bg-[#245e63]"
                  }`}
                >
                  <a href="#contact">Détails <ArrowRight className="w-3.5 h-3.5 ml-1" /></a>
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Program modules */}
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-32">
            <span className="text-xs uppercase tracking-widest text-[#2c7a80] font-semibold">Programme officiel</span>
            <h3 className="font-display text-3xl md:text-4xl font-medium text-[#0e3a3f] leading-tight mt-3 mb-4">
              Les 3 épreuves professionnelles.
            </h3>
            <p className="text-[#0e3a3f]/70 leading-relaxed mb-6">
              Le référentiel officiel du CAP AEPE se compose de trois épreuves professionnelles obligatoires (EP1, EP2, EP3), préparées en profondeur au CFMPE.
            </p>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img src={heroImages.blocks} alt="Activité éveil" className="w-full h-full object-cover" style={{ objectPosition: "30% center" }} />
            </div>
          </div>
          <div className="lg:col-span-8 space-y-4">
            {programModules.map((m, i) => (
              <div key={m.code} className="group bg-white rounded-2xl border border-[#e6ded0] p-7 hover-lift">
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-[#2c7a80] to-[#4ba9b0] text-white flex items-center justify-center font-display text-lg font-semibold shadow-lg shadow-[#2c7a80]/20">
                    {m.code}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-display text-xl font-medium text-[#0e3a3f] mb-3">{m.title}</h4>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                      {m.topics.map((t) => (
                        <div key={t} className="flex items-start gap-2 text-sm text-[#0e3a3f]/75">
                          <BookOpen className="w-4 h-4 text-[#e8a04d] mt-0.5 flex-shrink-0" />
                          <span>{t}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
